/**
 * BolaController.ts — BOLA-E (Enhanced) ABR-алгоритм.
 *
 * Работает НЕ как кастомный abrController hls.js, а как хук-оверрайд:
 * слушает события hls.js и переопределяет уровень через hls.nextAutoLevel
 * после каждого загруженного фрагмента.
 *
 * Алгоритм основан на статье:
 *   "BOLA: Near-Optimal Bitrate Adaptation for Online Videos"
 *   (Spiteri, Urgaonkar, Sitaraman — IEEE/ACM ToN, 2020)
 *
 * Фазы:
 *   1. STARTUP  (буфер < порога) — throughput-based выбор
 *   2. STEADY   (буфер >= порога) — BOLA utility-maximisation
 *   3. CAP      — min(bola, throughput) для защиты от ребуферизации
 */

import Hls from 'hls.js';
import type { Events, FragLoadedData } from 'hls.js';

// ── Параметры ─────────────────────────────────────────────────────────────────

/** γ_p — параметр защиты минимального буфера в BOLA */
const GAMMA_P = 1.0;

/** Small startup buffer before switching from throughput startup to BOLA decisions. */
const STARTUP_BUFFER_S = 2.5;

/** Minimum completed fragment samples before trusting local throughput estimates. */
const MIN_THROUGHPUT_SAMPLES = 2;

/** Safety-factor для throughput-based выбора */
const THROUGHPUT_SAFETY = 0.85;

/** Коэффициент EWMA для быстрой оценки */
const EWMA_ALPHA_FAST = 0.5;

/** Коэффициент EWMA для медленной оценки */
const EWMA_ALPHA_SLOW = 0.1;

// ── Helpers ───────────────────────────────────────────────────────────────────

function getBufferLength(media: HTMLMediaElement | null): number {
  if (!media || media.buffered.length === 0) return 0;
  const ct = media.currentTime;
  for (let i = 0; i < media.buffered.length; i++) {
    if (media.buffered.start(i) <= ct && media.buffered.end(i) >= ct) {
      return media.buffered.end(i) - ct;
    }
  }
  return 0;
}

/** Максимальный уровень, чей битрейт укладывается в bandwidth × safety */
function throughputLevel(bitrates: number[], estimate: number): number {
  const safeBw = estimate * THROUGHPUT_SAFETY;
  let level = 0;
  for (let i = 0; i < bitrates.length; i++) {
    if (bitrates[i] <= safeBw) level = i;
  }
  return level;
}

/**
 * BOLA-BASIC: score_m = (V·(v_m + γ_p) − Q) / S_m
 * Выбираем уровень с максимальным score.
 */
function bolaScore(
  bitrates: number[],
  bufQ: number,
  decisionWindow: number,
): number {
  const minBr = Math.min(...bitrates);
  const v = bitrates.map((b) => Math.log(b / minBr));
  const vMax = Math.max(...v);
  const V = decisionWindow / (vMax + GAMMA_P);

  let bestLevel = 0;
  let bestScore = -Infinity;
  for (let i = 0; i < bitrates.length; i++) {
    const score = (V * (v[i] + GAMMA_P) - bufQ) / bitrates[i];
    if (score > bestScore) {
      bestScore = score;
      bestLevel = i;
    }
  }
  return bestLevel;
}

// ── Attach / detach ───────────────────────────────────────────────────────────

export interface BolaHandle {
  destroy(): void;
}

/**
 * Подключает BOLA-E к экземпляру hls.js.
 *
 * - После каждого FRAG_LOADED обновляет оценку bandwidth и
 *   переопределяет `hls.nextAutoLevel` на основе BOLA.
 * - Каждые 500 мс дополнительно устанавливает уровень
 *   (страховка на случай пропуска события).
 */
export function attachBola(hls: Hls): BolaHandle {
  let estimate = hls.config.abrEwmaDefaultEstimate ?? 5_000_000;
  let ewmaFast = 0;
  let ewmaSlow = 0;
  let samples = 0;

  const updateEstimate = (bps: number) => {
    if (samples === 0) {
      ewmaFast = bps;
      ewmaSlow = bps;
    } else {
      ewmaFast = EWMA_ALPHA_FAST * bps + (1 - EWMA_ALPHA_FAST) * ewmaFast;
      ewmaSlow = EWMA_ALPHA_SLOW * bps + (1 - EWMA_ALPHA_SLOW) * ewmaSlow;
    }
    samples++;
    estimate = Math.min(ewmaFast, ewmaSlow);
  };

  const computeLevel = (): number => {
    const levels = hls.levels;
    if (!levels || levels.length <= 1) return 0;

    const bufQ = getBufferLength(hls.media);
    const bitrates = levels.map((l) => l.bitrate);
    const tpLevel = throughputLevel(bitrates, estimate);

    // Startup: keep throughput-based selection until buffer and samples are usable.
    if (bufQ < STARTUP_BUFFER_S || samples < MIN_THROUGHPUT_SAMPLES) {
      return tpLevel;
    }

    // Steady-state: BOLA + throughput cap
    const decisionWindow = (hls.config.maxBufferLength || 30) / 2;
    const bLevel = bolaScore(bitrates, bufQ, decisionWindow);
    return Math.min(bLevel, tpLevel);
  };

  // ── События ───────────────────────────────────────────────────────────────

  const onFragLoaded = (_ev: Events.FRAG_LOADED, data: FragLoadedData) => {
    const { first, start, end } = data.frag.stats.loading;
    const bytes = data.frag.stats.loaded || data.frag.stats.total;
    const loadStart = first > 0 ? first : start;
    const loadMs = end - loadStart;
    if (loadMs > 0 && bytes > 0) {
      updateEstimate((bytes * 8 * 1000) / loadMs);
    } else if (Number.isFinite(data.frag.stats.bwEstimate) && data.frag.stats.bwEstimate > 0) {
      updateEstimate(data.frag.stats.bwEstimate);
    } else if (Number.isFinite(hls.bandwidthEstimate) && hls.bandwidthEstimate > 0) {
      updateEstimate(hls.bandwidthEstimate);
    }

    // Переопределяем уровень для следующего фрагмента
    const level = computeLevel();
    hls.nextAutoLevel = level;
  };

  hls.on(Hls.Events.FRAG_LOADED, onFragLoaded);

  // Периодическая страховка — если hls.js запрашивает уровень между событиями
  const intervalId = window.setInterval(() => {
    if (hls.levels.length > 1) {
      hls.nextAutoLevel = computeLevel();
    }
  }, 500);

  return {
    destroy() {
      hls.off(Hls.Events.FRAG_LOADED, onFragLoaded);
      clearInterval(intervalId);
    },
  };
}
